import { QueryFunctionContext } from '@tanstack/react-query';
import moment from 'moment';
import { api } from '../services/api';
import { IData } from '../types/shared';
import { ICompany, IImposto } from './useCompanies';
import {
  ITaxAnalysisCSVResponse,
  ITaxAnalysisHistory,
  ITaxAnalysisSNHistory,
  ITaxAnalysisFilters,
  calculateTaxPercentage,
  calculateTotalTaxes,
} from '../types/taxAnalysis';

// Interface para intervalos de período
interface IPeriodInterval {
  start: string;
  end: string;
  label: string;
}

// Gerar intervalos mensais
const generateMonthlyIntervals = (start: moment.Moment, end: moment.Moment): IPeriodInterval[] => {
  const intervals: IPeriodInterval[] = [];
  const current = start.clone().startOf('month');

  while (current.isBefore(end) || current.isSame(end, 'month')) {
    const monthEnd = current.clone().endOf('month');
    const intervalStart = current.isBefore(start) ? start.clone() : current.clone();
    const intervalEnd = monthEnd.isAfter(end) ? end.clone() : monthEnd;

    intervals.push({
      start: intervalStart.format('YYYY-MM-DD'),
      end: intervalEnd.format('YYYY-MM-DD'),
      label: current.format('MMM/YY'),
    });
    current.add(1, 'month');
  }
  return intervals;
};

// Interface para resposta da rota de faturamento/impostos
export interface ITaxFaturamentoImpostoItem {
  TbEmpresa: ICompany;
  total_faturamento: number;
  pis: number;
  cofins: number;
  csll: number;
  irpj: number;
  icms: number;
  iss: number;
  iss_retido: number;
  total_impostos: number;
}

export interface ITotaisGerais {
  total_faturamento: number;
  pis: number;
  cofins: number;
  csll: number;
  irpj: number;
  icms: number;
  iss: number;
  iss_retido: number;
  total_impostos: number;
}

export interface ITaxFaturamentoImpostoResponse {
  empresas: ITaxFaturamentoImpostoItem[];
  total: number;
  totais_gerais: ITotaisGerais;
}

// Interface para cada atividade do Simples Nacional
export interface IAtividadeSN {
  anexo: number;
  secao?: number | null;
  tabela?: number | null;
  valor_guia: number;
  valor_ia: number;
  faixa?: number | null;
  aliquota_efetiva?: number | null;
  info_partilha?: string | null;
}

// Interface para resposta da rota de faturamento/Simples Nacional
export interface ITaxFaturamentoSNItem {
  TbEmpresa: ICompany;
  total_faturamento: number;
  vl_simples_nacional: number;
  vl_saida: number;
  vl_servico: number;
  vl_outros: number;
  iss: number;
  iss_retido: number;
  anexo_simples_nacional: number | null;
  secao_simples_nacional: number | null;
  tabela_simples_nacional: number | null;
  apuracoes?: IAtividadeSN[];
}

export interface ITotaisGeraisSN {
  total_faturamento: number;
  vl_simples_nacional: number;
}

export interface ITaxFaturamentoSNResponse {
  empresas: ITaxFaturamentoSNItem[];
  total: number;
  totais_gerais: ITotaisGeraisSN;
}

// Transformar dados da API para formato com percentuais calculados
export const transformCompanyToTaxAnalysis = (company: ICompany) => {
  const impostos = company.impostos_calculo || ({} as IImposto);
  const totalFaturamento = impostos.total_faturamento || 0;

  const pis = impostos.pis || 0;
  const cofins = impostos.cofins || 0;
  const csll = impostos.csll || 0;
  const irpj = impostos.irpj || 0;
  const icms = impostos.icms || 0;

  const totalImpostos = calculateTotalTaxes({ pis, cofins, csll, irpj, icms });

  return {
    id: company.id,
    external_id: company.external_id,
    razao_social: company.razao_social,
    cnpj: company.cnpj,
    total_faturamento: totalFaturamento,
    impostos: {
      pis,
      pis_percentual: calculateTaxPercentage(pis, totalFaturamento),
      cofins,
      cofins_percentual: calculateTaxPercentage(cofins, totalFaturamento),
      csll,
      csll_percentual: calculateTaxPercentage(csll, totalFaturamento),
      irpj,
      irpj_percentual: calculateTaxPercentage(irpj, totalFaturamento),
      icms,
      icms_percentual: calculateTaxPercentage(icms, totalFaturamento),
      total_impostos: totalImpostos,
      total_percentual: calculateTaxPercentage(totalImpostos, totalFaturamento),
    },
  };
};

// Transformar dados para Simples Nacional
export const transformCompanyToTaxAnalysisSN = (company: ICompany) => {
  const impostos = company.impostos_calculo || ({} as IImposto);
  const totalFaturamento = impostos.total_faturamento || 0;
  const vlSimplesNacional = impostos.vl_simples_nacional || 0;
  const faturamentoAcumulado = (impostos as any).faturamento_acumulado || 0;
  const vlSaida = (impostos as any).vl_saida || 0;
  const vlServico = (impostos as any).vl_servico || 0;
  const vlOutros = (impostos as any).vl_outros || 0;

  return {
    id: company.id,
    external_id: company.external_id,
    razao_social: company.razao_social,
    cnpj: company.cnpj,
    total_faturamento: totalFaturamento,
    faturamento_acumulado: faturamentoAcumulado,
    vl_simples_nacional: vlSimplesNacional,
    simples_nacional_percentual: calculateTaxPercentage(vlSimplesNacional, totalFaturamento),
    vl_saida: vlSaida,
    vl_servico: vlServico,
    vl_outros: vlOutros,
    anexo_simples_nacional: impostos.anexo_simples_nacional,
    secao_simples_nacional: impostos.secao_simples_nacional,
    tabela_simples_nacional: impostos.tabela_simples_nacional,
  };
};

export const useTaxAnalysis = () => {
  // Lista de empresas com dados de impostos (usando endpoint existente)
  const getTaxAnalysisCompanies = async (key: QueryFunctionContext): Promise<IData<ICompany[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
      params: {
        ...filters,
        screen_data: 'fiscal',
      },
    });
    return res.data;
  };

  // Lista de empresas Simples Nacional
  const getTaxAnalysisSNCompanies = async (
    key: QueryFunctionContext,
  ): Promise<IData<ICompany[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IData<ICompany[]>>('/api/v1/empresas/', {
      params: {
        ...filters,
        screen_data: 'simples_nacional',
      },
    });
    return res.data;
  };

  // Totais agregados de impostos
  const getTaxAnalysisTotals = async (key: QueryFunctionContext): Promise<any> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<any>('/api/v1/empresas/impostos/total/', {
      params: filters,
    });
    return res.data;
  };

  // Historico mensal para graficos de evolucao (busca multiplos meses)
  const getTaxAnalysisHistory = async (
    key: QueryFunctionContext,
  ): Promise<ITaxAnalysisHistory[]> => {
    const { companyId, months = 6 } = (key.queryKey?.[1] as any) ?? {};

    if (!companyId) return [];

    const currentDate = new Date();

    // Criar array de promessas para buscar todos os meses em paralelo
    const promises = Array.from({ length: months }, (_, i) => {
      const monthOffset = months - 1 - i;
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - monthOffset, 1);
      const dateStart = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      const dateEnd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${lastDay}`;

      return api
        .get<any>(`/api/v1/empresas/${companyId}/`, {
          params: {
            screen_data: 'fiscal',
            date_start: dateStart,
            date_end: dateEnd,
          },
        })
        .then(res => {
          const company = res.data?.data || res.data;
          if (!company) return null;

          const impostos = company.impostos_calculo || {};
          const totalFaturamento = impostos.total_faturamento || 0;
          const pis = impostos.pis || 0;
          const cofins = impostos.cofins || 0;
          const csll = impostos.csll || 0;
          const irpj = impostos.irpj || 0;
          const icms = impostos.icms || 0;
          const totalImpostos = pis + cofins + csll + irpj + icms;

          return {
            mes: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
            total_faturamento: totalFaturamento,
            pis,
            pis_percentual: calculateTaxPercentage(pis, totalFaturamento),
            cofins,
            cofins_percentual: calculateTaxPercentage(cofins, totalFaturamento),
            csll,
            csll_percentual: calculateTaxPercentage(csll, totalFaturamento),
            irpj,
            irpj_percentual: calculateTaxPercentage(irpj, totalFaturamento),
            icms,
            icms_percentual: calculateTaxPercentage(icms, totalFaturamento),
            total_impostos: totalImpostos,
            total_percentual: calculateTaxPercentage(totalImpostos, totalFaturamento),
          } as ITaxAnalysisHistory;
        })
        .catch(() => null);
    });

    const results = await Promise.all(promises);

    // Filtrar nulls e retornar apenas meses com dados validos
    return results.filter((item): item is ITaxAnalysisHistory => item !== null);
  };

  // Buscar faturamento e impostos totais por empresa (nova rota)
  const getTaxAnalysisFaturamentoImpostos = async (
    key: QueryFunctionContext,
  ): Promise<ITaxFaturamentoImpostoResponse> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<ITaxFaturamentoImpostoResponse>(
      '/api/v1/empresas/fiscal/total-faturanmento-imposto/',
      { params: filters },
    );
    return res.data;
  };

  // Buscar faturamento e Simples Nacional por empresa (nova rota)
  const getTaxAnalysisSNFaturamento = async (
    key: QueryFunctionContext,
  ): Promise<ITaxFaturamentoSNResponse> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<ITaxFaturamentoSNResponse>(
      '/api/v1/empresas/fiscal/total-faturamento-simples-nacional/',
      { params: filters },
    );
    return res.data;
  };

  // Buscar histórico baseado no período filtrado com granularidade dinâmica
  const getTaxAnalysisHistoryByPeriod = async (
    key: QueryFunctionContext,
  ): Promise<ITaxAnalysisHistory[]> => {
    const { companyId, dateStart, dateEnd } = (key.queryKey?.[1] as any) ?? {};

    if (!companyId || !dateStart || !dateEnd) return [];

    const start = moment(dateStart);
    const end = moment(dateEnd);

    // Sempre usar granularidade mensal para meses isolados no gráfico
    const intervals: IPeriodInterval[] = generateMonthlyIntervals(start, end);

    // Buscar dados para cada intervalo usando a rota existente
    const promises = intervals.map(interval =>
      api
        .get<ITaxFaturamentoImpostoResponse>(
          '/api/v1/empresas/fiscal/total-faturanmento-imposto/',
          {
            params: {
              external_id: companyId,
              date_start: interval.start,
              date_end: interval.end,
            },
          },
        )
        .then(res => {
          const empresaData = res.data?.empresas?.[0];
          if (!empresaData) return null;

          const totalFaturamento = empresaData.total_faturamento || 0;
          const pis = empresaData.pis || 0;
          const cofins = empresaData.cofins || 0;
          const csll = empresaData.csll || 0;
          const irpj = empresaData.irpj || 0;
          const icms = empresaData.icms || 0;
          const iss = empresaData.iss || 0;
          const issRetido = empresaData.iss_retido || 0;
          // Calcula total_impostos incluindo ISS se não vier do backend
          const totalImpostos =
            empresaData.total_impostos || pis + cofins + csll + irpj + icms + iss + issRetido;

          return {
            mes: interval.label,
            total_faturamento: totalFaturamento,
            pis,
            pis_percentual: calculateTaxPercentage(pis, totalFaturamento),
            cofins,
            cofins_percentual: calculateTaxPercentage(cofins, totalFaturamento),
            csll,
            csll_percentual: calculateTaxPercentage(csll, totalFaturamento),
            irpj,
            irpj_percentual: calculateTaxPercentage(irpj, totalFaturamento),
            icms,
            icms_percentual: calculateTaxPercentage(icms, totalFaturamento),
            iss,
            iss_percentual: calculateTaxPercentage(iss, totalFaturamento),
            iss_retido: issRetido,
            iss_retido_percentual: calculateTaxPercentage(issRetido, totalFaturamento),
            total_impostos: totalImpostos,
            total_percentual: calculateTaxPercentage(totalImpostos, totalFaturamento),
          } as ITaxAnalysisHistory;
        })
        .catch(() => null),
    );

    const results = await Promise.all(promises);

    // Filtrar nulls e retornar apenas intervalos com dados válidos
    return results.filter((item): item is ITaxAnalysisHistory => item !== null);
  };

  // Exportar análise de impostos para CSV (Lucro Presumido/Real)
  const exportTaxAnalysisToCSV = async (
    filters: ITaxAnalysisFilters,
  ): Promise<ITaxAnalysisCSVResponse> => {
    const endpoint = '/api/v1/empresas/fiscal/total-faturanmento-imposto/export/';

    // Monta os parâmetros de exportação sem o size (backend usa default grande)
    const exportParams: Record<string, any> = { ...filters, page: 1 };
    delete exportParams.size;

    const res = await api.get(endpoint, { params: exportParams });

    return res.data.empresas;
  };

  // Buscar histórico de Simples Nacional baseado no período filtrado (mês a mês)
  const getTaxAnalysisSNHistoryByPeriod = async (
    key: QueryFunctionContext,
  ): Promise<ITaxAnalysisSNHistory[]> => {
    const { companyId, dateStart, dateEnd } = (key.queryKey?.[1] as any) ?? {};

    if (!companyId || !dateStart || !dateEnd) return [];

    const start = moment(dateStart);
    const end = moment(dateEnd);

    // Sempre usar granularidade mensal para meses isolados no gráfico
    const intervals: IPeriodInterval[] = generateMonthlyIntervals(start, end);

    // Buscar dados para cada intervalo usando a rota de SN
    const promises = intervals.map(interval =>
      api
        .get<ITaxFaturamentoSNResponse>(
          '/api/v1/empresas/fiscal/total-faturamento-simples-nacional/',
          {
            params: {
              external_id: companyId,
              date_start: interval.start,
              date_end: interval.end,
            },
          },
        )
        .then(res => {
          const empresaData = res.data?.empresas?.[0];
          if (!empresaData) return null;

          const totalFaturamento = empresaData.total_faturamento || 0;
          const vlSimplesNacional = empresaData.vl_simples_nacional || 0;
          const vlSaida = empresaData.vl_saida || 0;
          const vlServico = empresaData.vl_servico || 0;
          const vlOutros = empresaData.vl_outros || 0;

          return {
            mes: interval.label,
            total_faturamento: totalFaturamento,
            vl_simples_nacional: vlSimplesNacional,
            simples_nacional_percentual: calculateTaxPercentage(
              vlSimplesNacional,
              totalFaturamento,
            ),
            vl_saida: vlSaida,
            vl_servico: vlServico,
            vl_outros: vlOutros,
          } as ITaxAnalysisSNHistory;
        })
        .catch(() => null),
    );

    const results = await Promise.all(promises);

    // Filtrar nulls e retornar apenas intervalos com dados válidos
    return results.filter((item): item is ITaxAnalysisSNHistory => item !== null);
  };

  // Exportar análise de Simples Nacional para CSV
  const exportTaxAnalysisSNToCSV = async (
    filters: ITaxAnalysisFilters,
  ): Promise<ITaxAnalysisCSVResponse> => {
    // Usa a mesma rota de export, passando tipo_regime=simples_nacional
    const endpoint = '/api/v1/empresas/fiscal/total-faturanmento-imposto/export/';

    // Monta os parâmetros de exportação sem o size (backend usa default grande)
    const exportParams: Record<string, any> = {
      ...filters,
      tipo_regime: 'simples_nacional',
      page: 1,
    };
    delete exportParams.size;

    const res = await api.get(endpoint, { params: exportParams });

    return res.data.empresas;
  };

  return {
    getTaxAnalysisCompanies,
    getTaxAnalysisSNCompanies,
    getTaxAnalysisTotals,
    getTaxAnalysisHistory,
    getTaxAnalysisFaturamentoImpostos,
    getTaxAnalysisSNFaturamento,
    getTaxAnalysisHistoryByPeriod,
    getTaxAnalysisSNHistoryByPeriod,
    exportTaxAnalysisToCSV,
    exportTaxAnalysisSNToCSV,
    transformCompanyToTaxAnalysis,
    transformCompanyToTaxAnalysisSN,
  };
};
